import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';
import TrimNameInput from './TrimNameInput';

const TraversingDrapeForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;
  const [width, setWidth] = useState(iv?.widthInches != null ? String(iv.widthInches) : '');
  const [height, setHeight] = useState(iv?.heightInches != null ? String(iv.heightInches) : '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [fabricRepeatInches, setFabricRepeatInches] = useState(iv?.fabricRepeatInches != null ? String(iv.fabricRepeatInches) : '');
  const [fabricWidthInches, setFabricWidthInches] = useState(iv?.fabricWidthInches != null ? String(iv.fabricWidthInches) : '54');
  const [liningType, setLiningType] = useState<'standard' | 'blackout' | 'sheer' | 'none' | ''>(iv?.liningType || 'standard');
  const [interliningType, setInterliningType] = useState<'none' | 'bump' | 'flannel' | ''>(iv?.interliningType || 'none');
  const [hardwareType, setHardwareType] = useState<'hospital_track' | 'ctm' | 'rod_and_rings' | 'traversing_rod' | ''>(iv?.hardwareType || '');
  const [trimPlacement, setTrimPlacement] = useState<'none' | 'sides' | 'bottom' | 'sides_and_bottom' | ''>(iv?.trimPlacement || 'none');
  const [trimDescription, setTrimDescription] = useState<string>(iv?.trimDescription || '');
  const [operatingSystem, setOperatingSystem] = useState<'manual' | 'somfy_standard' | 'rollease' | ''>(iv?.operatingSystem || '');
  // Work-order-only fields (no pricing impact) — see CLAUDE.md "OUTPUTS THAT
  // ARE NOT ON QUOTE BUT NEEDED FOR WORKORDER". Rod Face defaults to Window
  // Width + 16" but tracks width changes only until the user overrides it by hand.
  const [rodFaceInches, setRodFaceInches] = useState(iv?.rodFaceInches != null ? String(iv.rodFaceInches) : '');
  const [overlapInches, setOverlapInches] = useState(iv?.overlapInches != null ? String(iv.overlapInches) : '3.5');
  const [returnInches, setReturnInches] = useState(iv?.returnInches != null ? String(iv.returnInches) : '4');
  const [hemSize, setHemSize] = useState<string>(iv?.hemSize || '6"D');
  const [pleatType, setPleatType] = useState<'top_pinch' | 'traditional' | 'inverted' | 'cartridge' | 'butterfly' | 'ripplefold' | ''>(iv?.pleatType || '');
  const [error, setError] = useState<string | null>(null);
  // Tracks the Rod Face value we last computed from width, so editing width
  // keeps Rod Face in sync until the user types a custom override.
  const [autoFilledRodFace, setAutoFilledRodFace] = useState<string | null>(
    iv?.rodFaceInches != null && iv?.widthInches != null && Number(iv.rodFaceInches) === Number(iv.widthInches) + 16
      ? String(iv.rodFaceInches)
      : null
  );

  useEffect(() => {
    const w = parseFloat(width);
    if (!w || w <= 0) return;
    const computed = String(w + 16);
    setRodFaceInches((prev) => {
      const isBlank = !prev.trim();
      const matchesPreviousAutoFill = autoFilledRodFace !== null && prev === autoFilledRodFace;
      return isBlank || matchesPreviousAutoFill ? computed : prev;
    });
    setAutoFilledRodFace(computed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      const w = parseFloat(width);
      const h = parseFloat(height);
      if (!w || w <= 0 || !h || h <= 0) {
        setError('Window width and floor-to-ceiling height must be greater than 0');
        return null;
      }
      setError(null);
      return {
        widthInches: w,
        heightInches: h,
        fabricDescription: fabricDescription.trim() || null,
        fabricRepeatInches: fabricRepeatInches ? parseFloat(fabricRepeatInches) : null,
        fabricWidthInches: fabricWidthInches ? parseFloat(fabricWidthInches) : 54,
        liningType: liningType || 'standard',
        interliningType: interliningType || 'none',
        hardwareType: hardwareType || null,
        trimPlacement: trimPlacement || 'none',
        trimDescription: trimDescription.trim() || null,
        operatingSystem: operatingSystem || null,
        rodFaceInches: rodFaceInches ? parseFloat(rodFaceInches) : w + 16,
        overlapInches: overlapInches ? parseFloat(overlapInches) : 3.5,
        returnInches: returnInches ? parseFloat(returnInches) : 4,
        hemSize: hemSize.trim() || null,
        pleatType: pleatType || null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Window Width (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Floor to Ceiling Height (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Lining</label>
          <select
            className="input"
            value={liningType}
            onChange={(e) => setLiningType(e.target.value as 'standard' | 'blackout' | 'sheer' | 'none' | '')}
            disabled={props.disabled}
          >
            <option value="standard">Regular ($12.95/yd)</option>
            <option value="blackout">Blackout ($35.95/yd)</option>
            <option value="sheer">Sheer ($21.95/yd)</option>
            <option value="none">None</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Interlining</label>
          <select
            className="input"
            value={interliningType}
            onChange={(e) => setInterliningType(e.target.value as 'none' | 'bump' | 'flannel' | '')}
            disabled={props.disabled}
          >
            <option value="none">None</option>
            <option value="bump">Bump ($19.95/yd)</option>
            <option value="flannel">Flannel ($16.95/yd)</option>
          </select>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Hardware <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <select
            className="input"
            value={hardwareType}
            onChange={(e) => setHardwareType(e.target.value as 'hospital_track' | 'ctm' | 'rod_and_rings' | 'traversing_rod' | '')}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="hospital_track">Hospital Track ($25/ft)</option>
            <option value="ctm">CTM (Cut to Measure) ($75/pair)</option>
            <option value="rod_and_rings">Rod & Rings</option>
            <option value="traversing_rod">Traversing Rod</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Operating System</label>
          <select
            className="input"
            value={operatingSystem}
            onChange={(e) => setOperatingSystem(e.target.value as 'manual' | 'somfy_standard' | 'rollease' | '')}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="manual">Manual</option>
            <option value="somfy_standard">Somfy ($450)</option>
            <option value="rollease">Rollease ($135)</option>
          </select>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Trim</label>
          <select
            className="input"
            value={trimPlacement}
            onChange={(e) => setTrimPlacement(e.target.value as 'none' | 'sides' | 'bottom' | 'sides_and_bottom' | '')}
            disabled={props.disabled}
          >
            <option value="none">None</option>
            <option value="sides">Leading Edges</option>
            <option value="bottom">Along the Bottom</option>
            <option value="sides_and_bottom">Both (Leading Edges and Bottom)</option>
          </select>
        </div>
        <TrimNameInput value={trimDescription} onChange={setTrimDescription} disabled={props.disabled} />
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Repeat (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            placeholder="Pattern repeat, if any"
            value={fabricRepeatInches}
            onChange={(e) => setFabricRepeatInches(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Rod Face (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(default: width + 16")</span></label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={rodFaceInches}
            onChange={(e) => setRodFaceInches(e.target.value)}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Hem Size</label>
          <input
            type="text"
            className="input"
            placeholder='e.g., 6"D'
            value={hemSize}
            onChange={(e) => setHemSize(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Returns (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={returnInches}
            onChange={(e) => setReturnInches(e.target.value)}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Overlaps (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={overlapInches}
            onChange={(e) => setOverlapInches(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Pleat Type <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <select
            className="input"
            value={pleatType}
            onChange={(e) => setPleatType(e.target.value as typeof pleatType)}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="top_pinch">Top Pinch</option>
            <option value="traditional">Traditional</option>
            <option value="inverted">Inverted</option>
            <option value="cartridge">Cartridge</option>
            <option value="butterfly">Butterfly</option>
            <option value="ripplefold">Ripplefold</option>
          </select>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Width (inches)</label>
          <input
            type="number"
            min="0"
            step="0.25"
            className="input"
            value={fabricWidthInches}
            onChange={(e) => setFabricWidthInches(e.target.value)}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={fabricDescription}
            onChange={(name, item) => {
              setFabricDescription(name);
              if (item?.widthInches) {
                setFabricWidthInches(String(item.widthInches));
              }
            }}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default TraversingDrapeForm;
